import React, { useState } from "react";
import { toast } from "react-toastify";
import Table from "../../components/Table";
import EditClientModal from "./Components/EditClientModal";
import BulkLedgerModal from "./Components/BulkLedgerModal";
import ValidateLedgerModal from "./Components/ValidateLedgerModal";
import LedgerStatusModal from "./Components/LedgerStatusModal";
import AddNewLedgerUpload from "./Components/AddNewLedgerUpload";
import Pagination from "../../components/Pagination";
import {
  documentStatusOptions,
  approveStatusOptions,
  msmeStatusOptions,
  entityCategoryOptions,
  getEntityListColumns,
  useEntityList,
  downloadEntityData,
} from "./TableData";
import {
  LOB_OPTIONS_CLIENT,
  LOB_OPTIONS_SERVICE_PARTNER,
  LOB_OPTIONS_ALL,
  AA_DEPT_MAP,
} from "../CreateNew/CreateNewForm/Clients/ClientFields";
import dataNotFound from "../../assets/images/allList.png";
import { downloadTemplateAction } from "../../actions/downloadTemplateAction";
import { usePermissions } from "../../utils/roleUtils";
import "./index.scss";
import { getUserData } from "../../utils/authnicate";
import { Tooltip } from "@mui/material";
import uploadExcelForBulkSearchAction from "../../actions/uploadExcelFileBulkSearchAction";
import DownloadOtpModal from "../../components/DownloadOtpModal";
import { downloadEntityExcelAction } from "../../actions/VerifyOtpAction";
import BulkSearchModal from "./Components/BulkSearch";

const ClientList = () => {
  const { isFinance } = usePermissions();
  const canSelect = isFinance; // Only Finance team can select entities

  // ── Selection state (Finance only) ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkLedgerOpen, setBulkLedgerOpen] = useState(false);
  const [bulkLedgerOpenNew, setBulkLedgerOpenNew] = useState(false);

  // ── Ledger status modal (Finance — verified entities) ─────────────────────
  const [ledgerStatusOpen, setLedgerStatusOpen] = useState(false);
  const [ledgerStatusRow, setLedgerStatusRow] = useState(null);
  const [loading, setLoading] = useState(false);
  // ── Bulk search state (overrides normal table data when active) ───────────
  const [bulkEntities, setBulkEntities] = useState(null);
  const [bulkPagination, setBulkPagination] = useState(null);
  // ── Bulk search modal ─────────────────────────────────────────────────────
  const [bulkSearchOpen, setBulkSearchOpen] = useState(false);

  const handleLedgerClick = (row) => {
    setLedgerStatusRow(row);
    setLedgerStatusOpen(true);
  };

  // ── Add New Ledger modal (Finance) ────────────────────────────────────────
  const [addNewLedgerOpen, setAddNewLedgerOpen] = useState(false);
  const [addNewLedgerRow, setAddNewLedgerRow] = useState(null);

  const handleAddLedgerClick = (row) => {
    setAddNewLedgerRow(row);
    setAddNewLedgerOpen(true);
  };

  const getRowId = (row) =>
    row.entity?.entityPrimaryId || row.entity?.id || row.entity?.entityId;

  const handleSelectRow = (id, row, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
    setSelectedRows((prev) =>
      checked ? [...prev, row] : prev.filter((r) => getRowId(r) !== id),
    );
  };

  const handleSelectAll = (checked, data) => {
    if (checked) {
      const newIds = new Set(data.map(getRowId));
      setSelectedIds(newIds);
      setSelectedRows(data);
    } else {
      setSelectedIds(new Set());
      setSelectedRows([]);
    }
  };

  // Reset selection after bulk ledger upload
  const handleResetSelection = () => {
    setSelectedIds(new Set());
    setSelectedRows([]);
  };

  // Download template for selected entities via API (returns xlsx)
  const handleDownloadSelected = async () => {
    if (selectedRows.length === 0) return;
    const ids = selectedRows
      .map((row) => row.entity?.entityPrimaryId || row.entity?.id)
      .filter(Boolean);
    const result = await downloadTemplateAction(ids);
    if (!result.success) {
      toast.error(result.message || "Failed to download template");
    }
  };

  const loggedInUser = getUserData();

  const defCreatedByLoggedEmail = loggedInUser.email;

  const loggedinUserRole = loggedInUser.roles;

  const isSpoc = loggedinUserRole?.some(
    (role) => role.toLowerCase() !== "admin" || "",
  );
  // console.log("USER : ", isSpoc, loggedinUserRole);

  // ── Dashboard counts for stat cards ──────────────────────────────────────
  // const { counts, isLoading: countsLoading } = useDashboardCounts();

  const {
    isEditModalOpen,
    selectedClient,
    isLoading,
    handleEdit,
    handleSaveClient,
    handleApproveClient,
    handleDeficiencyClient,
    handleCloseModal,
    handleDelete,
    handleResendClient,
    currentEntities,
    pageSize,
    handleClearFilter,
    pagination,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    entityCategory,
    setEntityCategory,
    documentStatus,
    setDocumentStatus,
    approveStatus,
    setApproveStatus,
    lobType,
    setLobType,
    aaDepartmentFunction,
    setAaDepartmentFunction,
    msmeStatus,
    setMsmeStatus,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dateError,
  } = useEntityList();

  // ── Dynamic LOB + Dept options based on selected category/LOB ────────────
  const resolvedLobOptions = (() => {
    const cat = (entityCategory || "").toLowerCase().trim();
    if (cat === "client") {
      return [{ value: "", label: "LOB Type" }, ...LOB_OPTIONS_CLIENT.slice(1)];
    }
    if (cat === "service partner") {
      return [
        { value: "", label: "LOB Type" },
        ...LOB_OPTIONS_SERVICE_PARTNER.slice(1),
      ];
    }
    return [{ value: "", label: "LOB Type" }, ...LOB_OPTIONS_ALL.slice(1)];
  })();

  const resolvedDeptOptions = (() => {
    const cat = (entityCategory || "").toLowerCase().trim();
    const lob = (lobType || "").toLowerCase().trim();
    const deptMap = AA_DEPT_MAP[cat] || AA_DEPT_MAP[entityCategory] || {};
    const opts = deptMap[lob];
    if (opts && opts.length > 1) {
      return [{ value: "", label: "AA Department" }, ...opts.slice(1)];
    }
    // Flatten all dept options for the category when no LOB selected
    if (!lob) {
      const allOpts = Object.values(deptMap).flat();
      const unique = [
        { value: "", label: "AA Department" },
        ...Array.from(
          new Map(
            allOpts.filter((o) => o.value).map((o) => [o.value, o]),
          ).values(),
        ),
      ];
      return unique.length > 1
        ? unique
        : [{ value: "", label: "AA Department" }];
    }
    return [{ value: "", label: "AA Department" }];
  })();

  const handleBulkSearch = async (file) => {
    if (!file) return;
    try {
      setLoading(true);
      const result = await uploadExcelForBulkSearchAction(file);
      if (result.success) {
        if (result.data.length === 0) {
          toast.info("No matching entities found for the uploaded file.");
        } else {
          toast.success(
            `Bulk search complete — ${result.totalRecords} record${result.totalRecords !== 1 ? "s" : ""} found.`,
          );
          setBulkEntities(result.data);
          setBulkPagination(result.pagination);
          setBulkSearchOpen(false); // close modal on success
        }
      } else {
        toast.error(result.message || "Bulk search failed. Please try again.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload Excel file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearBulkSearch = () => {
    setBulkEntities(null);
    setBulkPagination(null);
  };

  // ── Download OTP modal ────────────────────────────────────────────────────
  const [downloadOtpOpen, setDownloadOtpOpen] = useState(false);

  const openDownloadOtp = () => setDownloadOtpOpen(true);

  // Called after OTP is verified — do the actual download
  const handleDownloadAfterOtp = async () => {
    if (bulkEntities && bulkEntities.length > 0) {
      // Bulk search is active — download the bulk result as CSV
      const { toCSV, triggerDownload, ALL_COLS } =
        await import("../../utils/downloadUtils");
      const rawRows = bulkEntities.map((row) => row.entity || {});
      const csv = toCSV(rawRows, ALL_COLS);
      triggerDownload(
        csv,
        `bulk_search_${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } else {
      // Normal download — call the API
      const result = await downloadEntityExcelAction();
      if (!result.success) {
        toast.error(result.message || "Download failed");
      }
    }
  };

  return (
    <div className="cl-page">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="cl-header">
        <div className="cl-header__left">
          <h1 className="cl-header__title">All Clients / Service Partner</h1>
          <p className="cl-header__sub">
            View and manage all client and service partner records.
          </p>
        </div>
        <div className="cl-header__right">
          {selectedIds.size > 0 && (
            <button
              className="cl-btn cl-btn--outline-green"
              onClick={handleDownloadSelected}
              title={`Download ${selectedIds.size} selected entities`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Selected
              <span className="cl-btn__badge">{selectedIds.size}</span>
            </button>
          )}
          {isFinance && (
            <button
              className="cl-btn cl-btn--primary"
              onClick={() => setBulkLedgerOpenNew(true)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              Validate Ledger Template
            </button>
          )}
        </div>
      </div>

      {/* ── Filter card ─────────────────────────────────────────────────────── */}
      <div className="cl-filter-card">
        {/* Row 1: Search + Download */}
        <div className="cl-filter-row cl-filter-row--search">
          <div className="cl-search">
            <svg
              className="cl-search__icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="cl-search__input"
              placeholder="Search by entity name, email, mobile, entity ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="cl-search__clear"
                onClick={() => setSearchQuery("")}
              >
                ✕
              </button>
            )}
          </div>
          {!isSpoc && (
            <>
              {/* Bulk Search button — opens modal */}
              <Tooltip
                title="Upload Excel file for bulk search"
                placement="top"
                arrow
              >
                <button
                  className="cl-btn cl-btn--upload-to-bulk-search"
                  onClick={() => setBulkSearchOpen(true)}
                  disabled={loading}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Bulk Search
                </button>
              </Tooltip>

              {/* Download button */}
              <button
                className="cl-btn cl-btn--download"
                onClick={openDownloadOtp}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </button>
            </>
          )}
        </div>

        {/* Row 2: 4 dropdowns with floating labels */}
        <div className="cl-filter-row cl-filter-row--grid4">
          <div className="cl-float-field">
            <label className="cl-float-label">Entity Category</label>
            <select
              className="cl-float-select"
              value={entityCategory}
              onChange={(e) => {
                setEntityCategory(e.target.value);
                setLobType("");
                setAaDepartmentFunction("");
              }}
            >
              {entityCategoryOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cl-float-field">
            <label className="cl-float-label">LOB Type</label>
            <select
              className="cl-float-select"
              value={lobType}
              onChange={(e) => setLobType(e.target.value)}
            >
              {resolvedLobOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cl-float-field">
            <label className="cl-float-label">AA Department</label>
            <select
              className="cl-float-select"
              value={aaDepartmentFunction}
              onChange={(e) => setAaDepartmentFunction(e.target.value)}
            >
              {resolvedDeptOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cl-float-field">
            <label className="cl-float-label">Approval Status</label>
            <select
              className="cl-float-select"
              value={approveStatus}
              onChange={(e) => setApproveStatus(e.target.value)}
              disabled={isFinance}
            >
              {approveStatusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3: MSME + Doc Status + Date Range with floating labels */}
        <div className="cl-filter-row cl-filter-row--grid4">
          <div className="cl-float-field">
            <label className="cl-float-label">MSME Status</label>
            <select
              className="cl-float-select"
              value={msmeStatus}
              onChange={(e) => setMsmeStatus(e.target.value)}
            >
              {msmeStatusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cl-float-field">
            <label className="cl-float-label">All Documents Status</label>
            <select
              className="cl-float-select"
              value={documentStatus}
              onChange={(e) => setDocumentStatus(e.target.value)}
            >
              {documentStatusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="cl-float-field cl-float-field--date">
            <label className="cl-float-label">Date Range</label>
            <div className="cl-date-range">
              <input
                type="date"
                className={`cl-float-date${dateError ? " cl-float-date--error" : ""}`}
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="cl-date-sep">–</span>
              <input
                type="date"
                className={`cl-float-date${dateError ? " cl-float-date--error" : ""}`}
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {dateError && <span className="cl-date-error">{dateError}</span>}
          </div>
        </div>

        {/* Row 4: Clear filters */}
        <div className="cl-filter-row cl-filter-row--actions">
          <button className="cl-clear-btn" onClick={handleClearFilter}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-3.89" />
            </svg>
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table card ──────────────────────────────────────────────────────── */}
      <div className="cl-table-card">
        {/* Bulk search active banner */}
        {bulkEntities && (
          <div className="cl-bulk-banner">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span>
              Showing bulk search results —{" "}
              <strong>
                {bulkPagination?.totalItems ?? bulkEntities.length}
              </strong>{" "}
              record
              {(bulkPagination?.totalItems ?? bulkEntities.length) !== 1
                ? "s"
                : ""}{" "}
              found
            </span>
            <button
              className="cl-bulk-banner__clear"
              onClick={handleClearBulkSearch}
            >
              ✕ Clear bulk search
            </button>
          </div>
        )}

        {isLoading && !bulkEntities ? (
          <div className="cl-loading">
            <p>Loading entities…</p>
          </div>
        ) : (bulkEntities ?? currentEntities).length === 0 ? (
          <div className="cl-empty">
            <img
              src={dataNotFound}
              className="cl-empty__img"
              alt="No data found"
            />
          </div>
        ) : (
          <>
            <div className="cl-table-wrap">
              <Table
                columns={getEntityListColumns(isFinance)}
                data={bulkEntities ?? currentEntities}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onResend={handleResendClient}
                onLedger={handleLedgerClick}
                onAddLedger={handleAddLedgerClick}
                showPagination={false}
                selectable={canSelect}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                getRowId={getRowId}
              />
            </div>
            <Pagination
              currentPage={(bulkPagination ?? pagination).currentPage || 1}
              totalPages={(bulkPagination ?? pagination).totalPages || 1}
              totalItems={(bulkPagination ?? pagination).totalItems || 0}
              itemsPerPage={pageSize}
              onPageChange={(page) => !bulkEntities && setCurrentPage(page)}
              showInfo={true}
            />
          </>
        )}
      </div>

      <EditClientModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        clientData={selectedClient}
        onSave={handleSaveClient}
        onApprove={handleApproveClient}
        defCreatedBy={defCreatedByLoggedEmail}
        onDeficiency={handleDeficiencyClient}
      />
      {isFinance && (
        <BulkLedgerModal
          isOpen={bulkLedgerOpen}
          onClose={() => setBulkLedgerOpen(false)}
          selectedEntities={selectedRows}
          onSuccess={handleResetSelection}
        />
      )}
      {isFinance && (
        <ValidateLedgerModal
          isOpen={bulkLedgerOpenNew}
          onClose={() => setBulkLedgerOpenNew(false)}
          selectedEntities={selectedRows}
          onSuccess={handleResetSelection}
        />
      )}
      <LedgerStatusModal
        isOpen={ledgerStatusOpen}
        onClose={() => {
          setLedgerStatusOpen(false);
          setLedgerStatusRow(null);
        }}
        entityRow={ledgerStatusRow}
      />
      <AddNewLedgerUpload
        isOpen={addNewLedgerOpen}
        onClose={() => {
          setAddNewLedgerOpen(false);
          setAddNewLedgerRow(null);
        }}
        entityRow={addNewLedgerRow}
      />

      {/* ── Bulk Search Modal ────────────────────────────────────────────── */}
      <BulkSearchModal
        isOpen={bulkSearchOpen}
        onClose={() => setBulkSearchOpen(false)}
        onSearch={handleBulkSearch}
        loading={loading}
      />

      {/* ── Download OTP verification modal ──────────────────────────────── */}
      <DownloadOtpModal
        isOpen={downloadOtpOpen}
        onClose={() => setDownloadOtpOpen(false)}
        email={loggedInUser?.email || ""}
        onVerified={handleDownloadAfterOtp}
      />
    </div>
  );
};

export default ClientList;
